import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  MenuItem,
  Checkbox,
  ListItemText,
  CircularProgress,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DonutChart from "../components/charts/DonutChart";
import { Visibility, VisibilityOff, Print, FileDownload, Close } from "@mui/icons-material";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ReportSiblingNav from "../components/Reports/ReportSiblingNav";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import {
  reportsService,
  cancellationReportService,
  CancellationAnalysisResponse,
  CancellationTypeItem,
  ReportProfessionalItem,
  CancellingPatientItem,
  ScheduleDetailItem,
} from "../services/reportsService";
import { colors, typography } from "../theme/designSystem";
import { filterFieldSx } from "../theme/filterStyles";
import { maskName } from "../utils/nameMask";
import MonthSelect from "../components/Inputs/MonthSelect";

// Análise de Cancelamentos — lê o espelho do Bubble via API (read-only).
// Nomes (pacientes e profissionais) mascarados por padrão; impressão sempre mascarada.

const formatCurrency = (value: number): string =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatMonthLabel = (month: string): string => {
  const [year, monthNumber] = month.split("-");
  const names = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `${names[parseInt(monthNumber, 10) - 1]}/${year.slice(2)}`;
};

// Donut SVG no mesmo padrão dos gráficos do Dashboard (círculos com strokeDasharray)
const PIE_FALLBACK_COLORS = ["#FF6347", "#512E5F", "#FFCC00", "#adb5bd", "#03B4C6"];

interface DonutSegment {
  percent: number;
  offset: number;
  color: string;
}

const buildDonutSegments = (items: CancellationTypeItem[], total: number): DonutSegment[] => {
  let accumulated = 0;
  return items.map((item, index) => {
    const percent = (100 * item.count) / total;
    const segment = {
      percent,
      offset: -accumulated,
      color: item.color || PIE_FALLBACK_COLORS[index % PIE_FALLBACK_COLORS.length],
    };
    accumulated += percent;
    return segment;
  });
};

const currentCompetence = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const competenceBounds = (competence: string): { from: string; to: string } => {
  const [year, month] = competence.split("-").map((v) => parseInt(v, 10));
  const lastDay = new Date(year, month, 0).getDate();
  const mm = String(month).padStart(2, "0");
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(lastDay).padStart(2, "0")}` };
};

const cardStyle = {
  flex: "1 1 150px",
  minWidth: 150,
  minHeight: 96,
  background: "#fff",
  borderRadius: 2,
  border: "1px solid #e9ecef",
  p: 2,
  display: "flex",
  flexDirection: "column" as const,
  justifyContent: "space-between",
};

const sectionStyle = {
  background: "#fff",
  borderRadius: 2,
  border: "1px solid #e9ecef",
  p: 2,
  mb: 3,
};

const ReportCancellations: React.FC = () => {
  const { navigateTo, getParam } = useRouter();
  const { user } = useAuth();

  // filtros vindos de outra página de relatório (navegação preserva o período)
  const paramFrom = getParam("from");
  const paramTo = getParam("to");
  const initialBounds = competenceBounds(currentCompetence());
  const [competence, setCompetence] = useState<string>(
    paramFrom && paramTo ? getParam("competence") ?? "" : currentCompetence()
  );
  const [fromDate, setFromDate] = useState<string>(paramFrom ?? initialBounds.from);
  const [toDate, setToDate] = useState<string>(paramTo ?? initialBounds.to);
  const [selectedProfessionals, setSelectedProfessionals] = useState<string[]>([]);
  const [showInactiveProfessionals, setShowInactiveProfessionals] = useState(false);
  const [showNames, setShowNames] = useState<boolean>(false);
  // modal com os cancelamentos e justificativas de um paciente do Top 20
  const [patientModal, setPatientModal] = useState<CancellingPatientItem | null>(null);
  const [patientRows, setPatientRows] = useState<ScheduleDetailItem[]>([]);
  const [patientLoading, setPatientLoading] = useState<boolean>(false);

  const [professionals, setProfessionals] = useState<ReportProfessionalItem[]>([]);
  const visibleProfessionals = professionals.filter((p) => (p.active ?? true) || showInactiveProfessionals);
  const [data, setData] = useState<CancellationAnalysisResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const displayName = (name: string): string => (showNames ? name : maskName(name));

  // filtros reativos: qualquer alteração dispara a consulta imediatamente (padrão da Agenda)
  const defaultBounds = competenceBounds(currentCompetence());
  const hasFilterChanges =
    competence !== currentCompetence() ||
    fromDate !== defaultBounds.from ||
    toDate !== defaultBounds.to ||
    selectedProfessionals.length > 0;

  const handleCompetenceChange = (value: string) => {
    setCompetence(value);
    if (value) {
      const bounds = competenceBounds(value);
      setFromDate(bounds.from);
      setToDate(bounds.to);
      loadData({ from: bounds.from, to: bounds.to, professionalIds: selectedProfessionals });
    }
  };

  const handleFromDateChange = (value: string) => {
    setFromDate(value);
    setCompetence("");
    if (value && toDate) {
      loadData({ from: value, to: toDate, professionalIds: selectedProfessionals });
    }
  };

  const handleToDateChange = (value: string) => {
    setToDate(value);
    setCompetence("");
    if (fromDate && value) {
      loadData({ from: fromDate, to: value, professionalIds: selectedProfessionals });
    }
  };

  const handleProfessionalsChange = (ids: string[]) => {
    setSelectedProfessionals(ids);
    loadData({ from: fromDate, to: toDate, professionalIds: ids });
  };

  const toggleInactiveProfessionals = () => {
    setShowInactiveProfessionals((prev) => {
      const next = !prev;
      if (!next) {
        const onlyActive = selectedProfessionals.filter(
          (id) => professionals.find((p) => p.externalId === id)?.active ?? true,
        );
        if (onlyActive.length !== selectedProfessionals.length) {
          handleProfessionalsChange(onlyActive);
        }
      }
      return next;
    });
  };

  const handleSelectAllProfessionals = () => {
    const ids =
      selectedProfessionals.length === visibleProfessionals.length
        ? []
        : visibleProfessionals.map((p) => p.externalId);
    handleProfessionalsChange(ids);
  };

  const loadData = useCallback(
    async (override?: { from: string; to: string; professionalIds: string[] }) => {
      const params = override ?? { from: fromDate, to: toDate, professionalIds: selectedProfessionals };
      if (!params.from || !params.to) return;
      setLoading(true);
      setError("");
      try {
        const response = await cancellationReportService.get(params);
        setData(response);
      } catch {
        setError("Não foi possível carregar os dados. Tente novamente.");
      } finally {
        setLoading(false);
      }
    },
    [fromDate, toDate, selectedProfessionals]
  );

  const clearFilters = () => {
    const competenceNow = currentCompetence();
    const bounds = competenceBounds(competenceNow);
    setCompetence(competenceNow);
    setFromDate(bounds.from);
    setToDate(bounds.to);
    setSelectedProfessionals([]);
    loadData({ from: bounds.from, to: bounds.to, professionalIds: [] });
  };

  const handlePrint = () => {
    const wasShowing = showNames;
    setShowNames(false);
    setTimeout(() => {
      window.print();
      setShowNames(wasShowing);
    }, 150);
  };

  const handleExport = async () => {
    setExporting(true);
    setError("");
    try {
      const blob = await reportsService.exportXlsx(
        { from: fromDate, to: toDate, professionalIds: selectedProfessionals },
        true
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `cancelamentos_${fromDate}_a_${toDate}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Não foi possível exportar a planilha. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    reportsService.getProfessionals().then(setProfessionals).catch(() => setProfessionals([]));
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalsCards = data
    ? [
        { label: "Agendamentos", value: String(data.totals.totalSchedules) },
        { label: "Cancelamentos", value: String(data.totals.totalCancelled), color: "#FF6347" },
        { label: "Taxa de cancelamento", value: `${data.totals.cancellationRate}%`, color: "#FF6347" },
        { label: "Canc. por paciente", value: String(data.totals.cancelledByPatient) },
        { label: "Canc. por profissional", value: String(data.totals.cancelledByProfessional) },
        { label: "Outros cancelamentos", value: String(data.totals.cancelledOther) },
        { label: "Impacto no período", value: formatCurrency(data.totals.grossImpact), color: "#FF6347" },
        {
          label: "Reagendados",
          value: `${data.totals.rescheduledCount} (${data.totals.rescheduledRate}%)`,
          color: colors.primary,
        },
        { label: "Valor salvo (reagend.)", value: formatCurrency(data.totals.valueSavedByRescheduling), color: "#32CD32" },
      ]
    : [];

  const rowStyle = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    py: 0.75,
    borderBottom: "1px solid #f1f3f5",
    flexWrap: "wrap" as const,
  };

  const maxImpactRate = Math.max(1, ...(data?.agendaImpact.map((i) => i.cancellationRate) ?? [1]));

  const openPatientModal = async (item: CancellingPatientItem) => {
    setPatientModal(item);
    setPatientRows([]);
    setPatientLoading(true);
    try {
      setPatientRows(await reportsService.getPatientCancellations(
        { from: fromDate, to: toDate, professionalIds: selectedProfessionals }, item.patientExternalId));
    } catch {
      setPatientRows([]);
    } finally {
      setPatientLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <style>{`
        @media print {
          .login-header, .login-footer-component, .no-print { display: none !important; }
          body { background: #fff !important; }
          main { margin-top: 0 !important; padding: 0 !important; background: #fff !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          /* tabelas largas: paisagem e sem scroll horizontal, para nada sair cortado */
          @page { size: A4 landscape; margin: 8mm; }
          [style*="overflow-x"], [style*="overflowX"] { overflow: visible !important; }
          table { min-width: 0 !important; width: 100% !important; font-size: 10px !important; }
          thead { display: table-header-group; }
          tr { break-inside: avoid; }
        }
      `}</style>

      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters>
          <Box
            sx={{
              mb: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
              <Typography variant="h4" className="c4u-page-header__title">
                Relatórios / Análise de Cancelamentos
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Fonte: agenda (sincronizada de hora em hora). Reagendado = cancelamento com novo
                agendamento vinculado (campo de reagendamento preenchido).
              </Typography>
            </Box>
            <Box className="no-print">
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
              <MonthSelect
                label="Competência"
                value={competence}
                onChange={handleCompetenceChange}
                sx={filterFieldSx}
              />
              <TextField
                size="small"
                type="date"
                label="De"
                value={fromDate}
                onChange={(e) => handleFromDateChange(e.target.value)}
                fullWidth
                sx={filterFieldSx}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                type="date"
                label="Até"
                value={toDate}
                onChange={(e) => handleToDateChange(e.target.value)}
                fullWidth
                sx={filterFieldSx}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                select
                size="small"
                label="Profissionais"
                value={selectedProfessionals}
                onChange={(e) => {
                  const value = e.target.value;
                  const ids = (typeof value === "string" ? value.split(",") : value).filter((v) => v !== "all");
                  handleProfessionalsChange(ids);
                }}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => {
                    const ids = selected as string[];
                    if (ids.length === 0) return "Todos";
                    if (ids.length > 2) return `${ids.length} selecionados`;
                    return professionals
                      .filter((p) => ids.includes(p.externalId))
                      .map((p) => displayName(p.name))
                      .join(", ");
                  },
                  displayEmpty: true,
                }}
                fullWidth
                sx={filterFieldSx}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem
                  value="all"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelectAllProfessionals();
                  }}
                  sx={{
                    backgroundColor: "#f8f9fa !important",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    borderBottom: "1px solid #e9ecef",
                  }}
                >
                  <Checkbox
                    checked={visibleProfessionals.length > 0 && selectedProfessionals.length === visibleProfessionals.length}
                    indeterminate={selectedProfessionals.length > 0 && selectedProfessionals.length < visibleProfessionals.length}
                    sx={{ padding: "4px 8px 4px 4px", "& .MuiSvgIcon-root": { fontSize: "1.2rem" } }}
                  />
                  <ListItemText
                    primary={`Selecionar Todos (${visibleProfessionals.length})`}
                    primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 600 }}
                  />
                </MenuItem>
                {/* Inativos sob demanda, como na Agenda: histórico de quem saiu */}
                <MenuItem
                  value="__inactive"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleInactiveProfessionals();
                  }}
                  sx={{
                    backgroundColor: "#f8f9fa !important",
                    fontSize: "0.85rem",
                    borderBottom: "1px solid #e9ecef",
                  }}
                >
                  <Checkbox
                    checked={showInactiveProfessionals}
                    sx={{ padding: "4px 8px 4px 4px", "& .MuiSvgIcon-root": { fontSize: "1.2rem" } }}
                  />
                  <ListItemText primary="Mostrar inativos" primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }} />
                </MenuItem>
                {visibleProfessionals.map((p) => (
                  <MenuItem key={p.externalId} value={p.externalId}>
                    <Checkbox size="small" checked={selectedProfessionals.includes(p.externalId)} />
                    <ListItemText primary={displayName(p.name) + (p.active === false ? " (inativo)" : "")} primaryTypographyProps={{ fontSize: 14 }} />
                  </MenuItem>
                ))}
              </TextField>
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges || loading} />
                <Tooltip title={showNames ? "Ocultar nomes" : "Exibir nomes"}>
                  <span>
                    <IconButton
                      className="c4u-btn-icon c4u-btn-icon--outline"
                      onClick={() => setShowNames((v) => !v)}
                      aria-label={showNames ? "Ocultar nomes" : "Exibir nomes"}
                    >
                      {showNames ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Imprimir (nomes sempre mascarados)">
                  <span>
                    <IconButton
                      className="c4u-btn-icon c4u-btn-icon--outline"
                      onClick={handlePrint}
                      disabled={loading || !data}
                      aria-label="Imprimir"
                    >
                      <Print fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Exportar planilha dos cancelamentos do período">
                  <span>
                    <IconButton
                      className="c4u-btn-icon c4u-btn-icon--outline"
                      onClick={handleExport}
                      disabled={exporting || loading}
                      aria-label="Exportar planilha"
                    >
                      {exporting ? <CircularProgress size={16} sx={{ color: colors.primary }} /> : <FileDownload fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>

            <ReportSiblingNav current="report-cancellations" competence={competence} from={fromDate} to={toDate} />

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ ...sectionStyle, textAlign: "center", color: "#dc3545" }}>{error}</Box>
          )}

          {!loading && !error && data && data.totals.totalCancelled === 0 && (
            <Box sx={{ ...sectionStyle, textAlign: "center", color: "#6c757d" }}>
              Nenhum cancelamento encontrado para os filtros selecionados.
            </Box>
          )}

          {!loading && !error && data && data.totals.totalCancelled > 0 && (
            <>
              {/* Cards de resumo */}
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
                {totalsCards.map((card) => (
                  <Box key={card.label} sx={cardStyle}>
                    <Typography sx={{ fontSize: 12, color: "#6c757d" }}>{card.label}</Typography>
                    <Typography sx={{ fontSize: 20, fontWeight: typography.fontWeight.semibold, color: card.color || "#2D3748" }}>
                      {card.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                {/* Cancelamentos por tipo */}
                <Box sx={{ ...sectionStyle, flex: "1 1 340px" }}>
                  <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 1 }}>
                    Cancelamentos por tipo
                  </Typography>
                  <Box sx={{ display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap" }}>
                    <Box sx={{ position: "relative", width: 140, height: 140, flexShrink: 0, mx: "auto" }}>
                      <svg width="140" height="140" viewBox="0 0 42 42" style={{ transform: "rotate(-90deg)" }}>
                        <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#e9ecef" strokeWidth="3" />
                        {buildDonutSegments(data.byType, data.totals.totalCancelled).map((segment, index) => (
                          <circle
                            key={index}
                            cx="21"
                            cy="21"
                            r="15.915"
                            fill="transparent"
                            stroke={segment.color}
                            strokeWidth="3"
                            strokeDasharray={`${segment.percent} ${100 - segment.percent}`}
                            strokeDashoffset={segment.offset}
                            strokeLinecap="round"
                          />
                        ))}
                      </svg>
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          textAlign: "center",
                        }}
                      >
                        <Typography variant="h5" sx={{ fontWeight: "bold", color: "#FF6347" }}>
                          {data.totals.totalCancelled}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          Total
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 260 }}>
                      {data.byType.map((item) => (
                        <Box key={item.type} sx={rowStyle}>
                          <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: item.color || "#adb5bd" }} />
                          <Typography sx={{ fontSize: 14, flex: 1 }}>{item.type}</Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: typography.fontWeight.semibold }}>
                            {item.count}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: "#6c757d", width: 48, textAlign: "right" }}>
                            {((100 * item.count) / data.totals.totalCancelled).toFixed(1)}%
                          </Typography>
                          <Typography sx={{ fontSize: 13, width: 110, textAlign: "right", color: colors.textPrimary }}>
                            {formatCurrency(item.grossValue)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>

                {/* Top pacientes que cancelaram */}
                <Box sx={{ ...sectionStyle, flex: "1 1 340px" }}>
                  <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 1 }}>
                    Top 20 pacientes que cancelaram
                  </Typography>
                  <Box sx={{ ...rowStyle, borderBottom: "2px solid #dee2e6", color: "#6c757d" }}>
                    <Typography sx={{ width: 24, fontSize: 12 }}>#</Typography>
                    <Typography sx={{ flex: 1, fontSize: 12 }}>Paciente</Typography>
                    <Typography sx={{ width: 60, textAlign: "right", fontSize: 12 }}>Qtde</Typography>
                    <Typography sx={{ width: 100, textAlign: "right", fontSize: 12 }}>Valor impactado</Typography>
                    <Typography sx={{ width: 110, textAlign: "right", fontSize: 12 }}>Com justificativa</Typography>
                  </Box>
                  {data.topPatients.map((item, index) => (
                    <Box
                      key={item.patientExternalId}
                      role="button"
                      tabIndex={0}
                      title="Ver os cancelamentos e as justificativas"
                      onClick={() => openPatientModal(item)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openPatientModal(item); } }}
                      sx={{ ...rowStyle, cursor: "pointer", "&:hover": { backgroundColor: "#f8f9fa" } }}
                    >
                      <Typography sx={{ width: 24, fontSize: 13, color: "#6c757d" }}>{index + 1}</Typography>
                      <Typography sx={{ flex: 1, fontSize: 14, color: colors.primary }}>{displayName(item.patientName)}</Typography>
                      <Typography sx={{ width: 60, textAlign: "right", fontSize: 14, fontWeight: typography.fontWeight.semibold }}>
                        {item.cancellations}
                      </Typography>
                      <Typography sx={{ width: 100, textAlign: "right", fontSize: 14 }}>{formatCurrency(item.grossImpact ?? 0)}</Typography>
                      <Typography sx={{ width: 110, textAlign: "right", fontSize: 14, color: item.withJustification > 0 ? "#32CD32" : "#FF6347" }}>
                        {item.withJustification > 0 ? `${item.withJustification} sim` : "não"}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Particular × Convênio e por plano (RN-222) */}
              {(data.byOrigin ?? []).length > 0 && (
                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  <Box sx={{ ...sectionStyle, flex: "1 1 320px", minWidth: 0 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 1 }}>
                      Cancelamentos: Particular × Convênio
                    </Typography>
                    <DonutChart
                      segments={(data.byOrigin ?? []).map((item) => ({
                        label: item.origin,
                        value: item.totalCancelled,
                        color: item.origin === "Convênio" ? "#6f42c1" : colors.primary,
                        sub: `${item.cancellationRate}% de ${item.totalSchedules} · ${formatCurrency(item.grossImpact)}`,
                      }))}
                      centerValue={String((data.byOrigin ?? []).reduce((sum, item) => sum + item.totalCancelled, 0))}
                      centerLabel="cancelados"
                    />
                  </Box>
                  <Box sx={{ ...sectionStyle, flex: "2 1 480px", minWidth: 0 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 1 }}>
                      Cancelamentos por convênio/plano
                    </Typography>
                    <Box sx={{ overflowX: "auto" }}>
                      <Box sx={{ minWidth: 720 }}>
                        <Box sx={{ ...rowStyle, flexWrap: "nowrap", borderBottom: "2px solid #dee2e6", color: "#6c757d" }}>
                          <Typography sx={{ flex: 2, fontSize: 12, minWidth: 150 }}>Plano</Typography>
                          <Typography sx={{ width: 80, textAlign: "right", fontSize: 12 }}>Agendados</Typography>
                          <Typography sx={{ width: 80, textAlign: "right", fontSize: 12 }}>Paciente</Typography>
                          <Typography sx={{ width: 90, textAlign: "right", fontSize: 12 }}>Profissional</Typography>
                          <Typography sx={{ width: 70, textAlign: "right", fontSize: 12 }}>Outros</Typography>
                          <Typography sx={{ width: 80, textAlign: "right", fontSize: 12 }}>Total</Typography>
                          <Typography sx={{ width: 60, textAlign: "right", fontSize: 12 }}>Taxa</Typography>
                          <Typography sx={{ width: 80, textAlign: "right", fontSize: 12 }}>Reagend.</Typography>
                          <Typography sx={{ width: 110, textAlign: "right", fontSize: 12 }}>Impacto</Typography>
                        </Box>
                        {(data.byPlan ?? []).map((item) => (
                          <Box key={`${item.plan}|${item.origin}`} sx={{ ...rowStyle, flexWrap: "nowrap" }}>
                            <Box sx={{ flex: 2, minWidth: 150 }}>
                              <Typography sx={{ fontSize: 14 }}>{item.plan}</Typography>
                              <Typography sx={{ fontSize: 11, color: item.origin === "Convênio" ? "#6f42c1" : colors.primary }}>{item.origin}</Typography>
                            </Box>
                            <Typography sx={{ width: 80, textAlign: "right", fontSize: 14 }}>{item.totalSchedules}</Typography>
                            <Typography sx={{ width: 80, textAlign: "right", fontSize: 14, color: "#FF6347" }}>{item.cancelledByPatient}</Typography>
                            <Typography sx={{ width: 90, textAlign: "right", fontSize: 14, color: "#512E5F" }}>{item.cancelledByProfessional}</Typography>
                            <Typography sx={{ width: 70, textAlign: "right", fontSize: 14, color: "#6c757d" }}>{item.cancelledOther}</Typography>
                            <Typography sx={{ width: 80, textAlign: "right", fontSize: 14, fontWeight: typography.fontWeight.semibold }}>{item.totalCancelled}</Typography>
                            <Typography sx={{ width: 60, textAlign: "right", fontSize: 13 }}>{item.cancellationRate}%</Typography>
                            <Typography sx={{ width: 80, textAlign: "right", fontSize: 14, color: "#32CD32" }}>{item.rescheduledCount}</Typography>
                            <Typography sx={{ width: 110, textAlign: "right", fontSize: 14 }}>{formatCurrency(item.grossImpact)}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Profissionais que cancelaram */}
              <Box sx={sectionStyle}>
                <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 1 }}>
                  Cancelamentos por profissional (com justificativa)
                </Typography>
                <Box sx={{ ...rowStyle, borderBottom: "2px solid #dee2e6", color: "#6c757d" }}>
                  <Typography sx={{ flex: 1, fontSize: 12, minWidth: 140 }}>Profissional</Typography>
                  <Typography sx={{ width: 60, textAlign: "right", fontSize: 12 }}>Qtde</Typography>
                  <Typography sx={{ width: 110, textAlign: "right", fontSize: 12 }}>Com justificativa</Typography>
                  <Typography sx={{ flex: 2, fontSize: 12, minWidth: 200 }}>Última justificativa</Typography>
                </Box>
                {data.professionalCancellations.map((item) => (
                  <Box key={item.professionalExternalId || item.professionalName} sx={rowStyle}>
                    <Typography sx={{ flex: 1, fontSize: 14, minWidth: 140 }}>{displayName(item.professionalName)}</Typography>
                    <Typography sx={{ width: 60, textAlign: "right", fontSize: 14, fontWeight: typography.fontWeight.semibold }}>
                      {item.cancellations}
                    </Typography>
                    <Typography sx={{ width: 110, textAlign: "right", fontSize: 14, color: item.withJustification > 0 ? "#32CD32" : "#FF6347" }}>
                      {item.withJustification > 0 ? `${item.withJustification} sim` : "não"}
                    </Typography>
                    <Typography sx={{ flex: 2, fontSize: 13, minWidth: 200, color: "#6c757d", fontStyle: item.lastJustification ? "normal" : "italic" }}>
                      {item.lastJustification || "—"}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Impacto na agenda dos profissionais */}
              <Box sx={sectionStyle}>
                <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 1 }}>
                  Impacto na agenda dos profissionais
                </Typography>
                <Box sx={{ overflowX: "auto" }}>
                  <Box sx={{ minWidth: 960 }}>
                    <Box sx={{ ...rowStyle, flexWrap: "nowrap", borderBottom: "2px solid #dee2e6", color: "#6c757d" }}>
                      <Typography sx={{ flex: 2, fontSize: 12, minWidth: 150 }}>Profissional</Typography>
                      <Typography sx={{ width: 90, textAlign: "right", fontSize: 12 }}>Agend. período</Typography>
                      <Typography sx={{ width: 85, textAlign: "right", fontSize: 12 }}>Canc. paciente</Typography>
                      <Typography sx={{ width: 80, textAlign: "right", fontSize: 12 }}>Canc. feriado</Typography>
                      <Typography sx={{ width: 95, textAlign: "right", fontSize: 12 }}>Canc. profissional</Typography>
                      <Typography sx={{ width: 60, textAlign: "right", fontSize: 12 }}>Outros</Typography>
                      <Typography sx={{ width: 80, textAlign: "right", fontSize: 12 }}>Total canc.</Typography>
                      <Typography sx={{ width: 150, fontSize: 12, pl: 2 }}>Taxa de cancelamento</Typography>
                      <Typography sx={{ width: 110, textAlign: "right", fontSize: 12 }}>Impacto R$</Typography>
                    </Box>
                    {data.agendaImpact.map((item) => (
                      <Box key={item.professionalExternalId || item.professionalName} sx={{ ...rowStyle, flexWrap: "nowrap" }}>
                        <Typography sx={{ flex: 2, fontSize: 14, minWidth: 150 }}>{displayName(item.professionalName)}</Typography>
                        <Typography sx={{ width: 90, textAlign: "right", fontSize: 14 }}>{item.totalSchedules}</Typography>
                        <Typography sx={{ width: 85, textAlign: "right", fontSize: 14 }}>{item.cancelledByPatient}</Typography>
                        <Typography sx={{ width: 80, textAlign: "right", fontSize: 14 }}>{item.cancelledByHoliday}</Typography>
                        <Typography sx={{ width: 95, textAlign: "right", fontSize: 14 }}>{item.cancelledByProfessional}</Typography>
                        <Typography sx={{ width: 60, textAlign: "right", fontSize: 14 }}>{item.cancelledOther}</Typography>
                        <Typography sx={{ width: 80, textAlign: "right", fontSize: 14, fontWeight: typography.fontWeight.semibold }}>
                          {item.totalCancelled}
                        </Typography>
                        <Box sx={{ width: 150, pl: 2, display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography sx={{ fontSize: 13, width: 48 }}>{item.cancellationRate}%</Typography>
                          <Box sx={{ flex: 1, height: 8, background: "#f1f3f5", borderRadius: 4 }}>
                            <Box
                              sx={{
                                width: `${(100 * item.cancellationRate) / maxImpactRate}%`,
                                height: "100%",
                                background: "#dc3545",
                                borderRadius: 4,
                              }}
                            />
                          </Box>
                        </Box>
                        <Typography sx={{ width: 110, textAlign: "right", fontSize: 14, color: "#FF6347" }}>
                          {formatCurrency(item.grossImpact)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Evolução mensal (períodos com mais de um mês) */}
              {data.monthly.length > 1 && (
                <Box sx={sectionStyle}>
                  <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 2 }}>
                    Evolução mensal dos cancelamentos
                  </Typography>
                  {data.monthly.map((m) => (
                    <Box key={m.month} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Typography sx={{ width: 58, fontSize: 12, color: "#6c757d" }}>
                        {formatMonthLabel(m.month)}
                      </Typography>
                      <Box sx={{ flex: 1, background: "#f1f3f5", borderRadius: 1, height: 22, position: "relative" }}>
                        <Box
                          sx={{
                            width: `${Math.min(100, m.cancellationRate)}%`,
                            background: "#dc3545",
                            height: "100%",
                            borderRadius: 1,
                            position: "absolute",
                          }}
                        />
                        <Typography sx={{ position: "relative", fontSize: 11, px: 1, lineHeight: "22px", color: "#2D3748" }}>
                          {m.totalCancelled}/{m.totalSchedules} · {m.cancellationRate}%
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
          </div>
        </Container>
      </Box>

      {/* Cancelamentos e justificativas do paciente escolhido no Top 20 */}
      <Dialog
        open={patientModal !== null}
        onClose={() => setPatientModal(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", boxShadow: "0 10px 30px rgba(0,0,0,0.3)", maxHeight: "90vh" } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: colors.white,
            padding: "1.5rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontSize: "1.4rem", minWidth: 0, overflowWrap: "anywhere" }}>
            Cancelamentos — {patientModal ? displayName(patientModal.patientName) : ""}
          </Typography>
          <IconButton onClick={() => setPatientModal(null)} aria-label="Fechar" sx={{ color: colors.white }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.5rem !important", paddingTop: "1.5rem !important" }}>
          {patientModal && (
            <Typography sx={{ fontSize: "0.875rem", color: colors.textSecondary, mb: 2 }}>
              {patientModal.cancellations} cancelamento(s) no período, {formatCurrency(patientModal.grossImpact ?? 0)} de valor impactado,{" "}
              {patientModal.withJustification} com justificativa.
            </Typography>
          )}
          {patientLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} sx={{ color: colors.primary }} />
            </Box>
          ) : patientRows.length === 0 ? (
            <Typography sx={{ fontSize: "0.875rem", color: colors.textSecondary, py: 2 }}>
              Nenhum cancelamento encontrado no período.
            </Typography>
          ) : (
            <Box className="c4u-table">
              <Box className="c4u-table__header">
                <Box sx={{ flex: "0 0 110px" }}>Data</Box>
                <Box sx={{ flex: "1 1 140px", minWidth: 0 }}>Profissional</Box>
                <Box className="c4u-col--md" sx={{ flex: "0 0 150px" }}>Tipo</Box>
                <Box sx={{ flex: "0 0 90px", textAlign: "right" }}>Valor</Box>
                <Box sx={{ flex: "2 1 180px", minWidth: 0 }}>Justificativa</Box>
              </Box>
              {patientRows.map((row) => (
                <Box key={row.scheduleExternalId} className="c4u-table__row" sx={{ alignItems: "flex-start" }}>
                  <Typography className="admin-plans-cell admin-plans-name" data-label="Data" sx={{ flex: "0 0 110px", fontSize: "0.875rem" }}>
                    {row.date} {row.startTime}
                  </Typography>
                  <Typography className="admin-plans-cell" data-label="Profissional" sx={{ flex: "1 1 140px", minWidth: 0, fontSize: "0.875rem", overflowWrap: "anywhere" }}>
                    {displayName(row.professionalName ?? "")}
                  </Typography>
                  <Typography className="admin-plans-cell c4u-col--md" data-label="Tipo" sx={{ flex: "0 0 150px", fontSize: "0.875rem" }}>
                    {row.confirmationStatus ?? ""}
                  </Typography>
                  <Typography className="admin-plans-cell" data-label="Valor" sx={{ flex: "0 0 90px", textAlign: { xs: "left", md: "right" }, fontSize: "0.875rem" }}>
                    {formatCurrency(row.plannedValue ?? 0)}
                  </Typography>
                  <Typography
                    className="admin-plans-cell"
                    data-label="Justificativa"
                    sx={{ flex: "2 1 180px", minWidth: 0, fontSize: "0.875rem", whiteSpace: "pre-wrap", overflowWrap: "anywhere", color: row.justification ? colors.textPrimary : "#adb5bd" }}
                  >
                    {row.justification || "sem justificativa"}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            padding: "1.5rem 2rem",
            borderTop: `1px solid ${colors.backgroundAlt}`,
            backgroundColor: colors.background,
            gap: "1rem",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setPatientModal(null)}
            sx={{ height: 40, textTransform: "none", borderColor: colors.primary, color: colors.primary }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default ReportCancellations;
